import { cn } from '@/lib/utils'
import * as React from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

export const Tooltip = ({ children, content, className, position = 'top' }: TooltipProps) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 })
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)

  const updateTooltipPosition = React.useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    }

    let x = 0
    let y = 0

    switch (position) {
      case 'top':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        y = triggerRect.top - tooltipRect.height - 8
        break
      case 'bottom':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
        y = triggerRect.bottom + 8
        break
      case 'left':
        x = triggerRect.left - tooltipRect.width - 8
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        break
      case 'right':
        x = triggerRect.right + 8
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
        break
    }

    // Keep tooltip within viewport bounds with smart repositioning
    if (x < 8) {
      x = 8
    } else if (x + tooltipRect.width > viewport.width - 8) {
      x = viewport.width - tooltipRect.width - 8
    }

    if (y < 8) {
      y = 8
    } else if (y + tooltipRect.height > viewport.height - 8) {
      y = viewport.height - tooltipRect.height - 8
    }

    setTooltipPosition({ x, y })
  }, [position])

  React.useEffect(() => {
    if (isVisible) {
      updateTooltipPosition()

      const handleResize = () => updateTooltipPosition()
      const handleScroll = () => updateTooltipPosition()

      window.addEventListener('resize', handleResize)
      window.addEventListener('scroll', handleScroll, true)

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [isVisible, updateTooltipPosition])

  const handleMouseEnter = () => {
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    setIsVisible(false)
  }

  const tooltipElement =
    isVisible && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'bg-popover text-popover-foreground fixed z-50 rounded-md px-3 py-1.5 text-xs',
              'border-border border shadow-lg dark:shadow-none',
              'shadow-[0_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0_10px_20px_-15px_rgba(22,_23,_24,_0.2)]',
              'pointer-events-none invisible opacity-0 transition-opacity duration-200',
              isVisible && 'visible opacity-100',
              className
            )}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y
            }}
          >
            {/* Arrow that looks like part of the tooltip */}
            <svg
              className={cn(
                'absolute h-1.5 w-3 overflow-visible',
                position === 'top' && 'top-full left-1/2 -mt-px -translate-x-1/2',
                position === 'bottom' && 'bottom-full left-1/2 -mb-px -translate-x-1/2 rotate-180',
                position === 'left' && 'top-1/2 left-full -ml-px -translate-y-1/2 -rotate-90',
                position === 'right' && 'top-1/2 right-full -mr-px -translate-y-1/2 rotate-90'
              )}
              viewBox="0 0 12 6"
            >
              {/* Arrow path with border */}
              <path
                d="M0 0 L6 6 L12 0"
                className="fill-popover stroke-border"
                strokeWidth="1"
                strokeLinejoin="round"
              />
            </svg>
            {content}
          </div>,
          document.getElementById('radix-portal-container') || document.body
        )
      : null

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {tooltipElement}
    </>
  )
}
